import React, { useEffect, useState } from "react";
import axiosInstance from "../../interceptors";
import { toast } from "../shared/toast";

// ── SampleDataBanner (prompt #1 Part A finale) ─────────────────────────────
// Renders on /admin/dashboard above the SetupChecklist when the workspace
// still contains sample data. One-click "Hide samples" wipes them via the
// DELETE /tenant/sample-data endpoint. After the wipe the banner removes
// itself.
//
// Status is fetched once on mount (cheap aggregate count endpoint). If
// the call fails or returns hasSamples=false, the banner is invisible.

export default function SampleDataBanner() {
  const [status, setStatus] = useState(null); // null | {hasSamples, counts}
  const [wiping, setWiping] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/job-posting/tenant/sample-data/status")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.success) setStatus(r.data);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => { cancelled = true; };
  }, []);

  if (hidden || !status || !status.hasSamples) return null;

  const wipe = async () => {
    setWiping(true);
    try {
      const r = await axiosInstance.delete("/job-posting/tenant/sample-data");
      const d = r.data?.deleted || {};
      const total = (d.jobs || 0) + (d.candidates || 0) + (d.rounds || 0) + (d.talentPools || 0);
      toast.success(`Removed ${total} sample row${total === 1 ? "" : "s"}`, {
        description: "Your workspace now only shows your real data.",
      });
      setHidden(true);
    } catch (e) {
      toast.error("Couldn't remove sample data", {
        description: e?.message || "Unknown error — try again.",
      });
    } finally {
      setWiping(false);
    }
  };

  const c = status.counts || {};
  // Human-readable summary: "1 job, 2 candidates, 1 talent pool"
  const parts = [];
  if (c.jobs > 0)        parts.push(`${c.jobs} job${c.jobs === 1 ? "" : "s"}`);
  if (c.candidates > 0)  parts.push(`${c.candidates} candidate${c.candidates === 1 ? "" : "s"}`);
  if (c.rounds > 0)      parts.push(`${c.rounds} round${c.rounds === 1 ? "" : "s"}`);
  if (c.talentPools > 0) parts.push(`${c.talentPools} talent pool${c.talentPools === 1 ? "" : "s"}`);
  const summary = parts.length > 0 ? parts.join(" · ") : "sample rows";

  return (
    <section
      aria-label="Sample data notice"
      className="rounded-card bg-vf-tint border border-vf-highlight px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
    >
      <div className="text-xl flex-shrink-0" aria-hidden="true">💡</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-vf-base">
          You're viewing sample data
        </div>
        <p className="text-xs text-vf-muted mt-0.5 m-0">
          We seeded {summary} so this workspace doesn't look empty on day one.
          Remove them whenever you're ready to work with your real data.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => setHidden(true)}
          className="text-xs text-vf-muted hover:text-vf-base px-2 py-1.5"
        >
          Keep them
        </button>
        <button
          type="button"
          onClick={wipe}
          disabled={wiping}
          className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-subtle text-vf-base hover:bg-vf-bg disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
        >
          {wiping ? "Removing…" : "Hide samples"}
        </button>
      </div>
    </section>
  );
}
